import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ADMIN } from 'src/app/constants/roles';
import { Observable } from 'rxjs';
import { Me } from '../models/me';
import { MeService } from './me.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private meService = inject(MeService);

  readonly BASE_URL = '/api/auth';
  me?: Me;

  // helper for building routes
  private buildRoute(path: string): string {
    return `${this.BASE_URL}/${path}`;
  }

  static buildOrgRole(organization_id: number, role: string) {
    return `org-${organization_id}:${role}`;
  }

  private getRolesFromMe(): string[] {
    if (!this.me) {
      return [];
    }

    return [
      ...(this.me.roles || []),
      ...this.me.memberships.map((m) =>
        AuthService.buildOrgRole(m.organization.id, m.role_name)
      ),
    ];
  }

  // get the roles for the current user
  getRoles() {
    if (this.me) {
      return this.getRolesFromMe();
    }
    return [];
  }

  async getCurrentUser(): Promise<void> {
    // fetch user info we have not already
    if (!this.me) {
      try {
        const me = await this.meService.get().toPromise();
        if (me) {
          this.setCurrentUser(me);
        }
      } catch (e) {
        console.error('failed to fetch current user.', e);
      }
    }
  }

  // determines if the user has any of the specified roles
  hasAnyRole(roles: string[]) {
    const userRoles: string[] = this.getRoles();
    return userRoles.some((r) => roles.includes(r));
  }

  // determines if the user has a specified role
  hasRole(role: string) {
    const roles: string[] = this.getRoles();
    return roles.some((r) => r === role);
  }

  async isAdmin() {
    if (!this.me) {
      await this.getCurrentUser();
    }
    return !!this.me && this.hasRole(ADMIN);
  }

  // determines whether or not the logged in user is an admin
  async isOrganizationAdmin(organizationId: number) {
    if (!this.me) {
      await this.getCurrentUser();
    }
    const role = AuthService.buildOrgRole(organizationId, ADMIN);
    return !!this.me && this.hasRole(role);
  }

  // clear the current user
  clearCurrentUser() {
    this.me = undefined;
  }

  // set current user
  setCurrentUser(me: Me) {
    this.me = me;
  }

  login(username: string, password: string): Observable<any> {
    const route = this.buildRoute('login');
    return this.http.post(route, { username, password }, httpOptions);
  }

  refreshToken(refreshToken: string) {
    const route = this.buildRoute('refresh_token');
    return this.http.post(route, { refresh_token: refreshToken }, httpOptions);
  }

  sendResetPasswordCode(username: string): Observable<any> {
    const route = this.buildRoute('reset_password');
    return this.http.post(route, { username }, httpOptions);
  }

  setNewPassword(
    username: string,
    password: string,
    code: string
  ): Observable<any> {
    const route = this.buildRoute('reset_password');
    return this.http.patch(route, { username, code, password }, httpOptions);
  }

  changePassword(
    username: string,
    new_password: string,
    session: string
  ): Observable<any> {
    const route = this.buildRoute('change_password_required');
    return this.http.post<any>(
      route,
      { username, new_password, session },
      httpOptions
    );
  }
}
